import { memo, useEffect, useMemo, useState } from "react";
import { useStore } from "../store/globalState";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BodyCopy } from "../components/BodyCopy";
import { Button, Layout } from "lola-framework-ui-test";
import { FloatingField } from "../components/FloatingField";
import { AuraLayout } from "../components/AuraLayout";
import { APP_ROUTES, TITLE_COLOR } from "../constants";
import {
  CARD_BRANDS,
  DEFAULT_ALLOWED_BRANDS,
  detectCardBrand,
  type CardBrandKey,
} from "../constants/card-brands";
import { useSessionStore } from "../store/useSessionStore";
import { useRouteWithToken } from "../hooks/useRouteWithToken";
import { useClaimLink } from "../hooks/useClaimLink";
import { useVirtualKeyboardOpen } from "../hooks/useVirtualKeyboardOpen";
import { useVisualViewportPin } from "../hooks/useVisualViewportPin";
import { getEvents, safeCapture } from "../analytics";
import {
  CARDHOLDER_NAME_INPUT_MAX_LENGTH,
  formatCardholderNameInput,
  isValidCardholderName,
} from "../utils/cardholderName";
import { ScrollFadeIndicator } from "../components/ScrollFadeIndicator";
import { blurActiveTextInput } from "../utils/blurActiveTextInput";
import { Navbar } from "../components/Navbar";

/**
 * Card data collected on this step, forwarded (via router state) to the billing
 * address step and finally tokenized + registered on the processing step.
 */
export interface CardStepFields {
  cardNumber: string;
  expiry: string;
  cardHolderName: string;
  cvv: string;
}

interface CardFormErrors {
  cardNumber?: string;
  expiry?: string;
  cardHolderName?: string;
  cvv?: string;
}

const formatCardNumber = (value: string): string =>
  value
    .replace(/\D/g, "")
    .slice(0, 19)
    .replace(/(.{4})/g, "$1 ")
    .trim();

const formatExpiry = (value: string): string => {
  const digits = value.replace(/\D/g, "").slice(0, 4);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}/${digits.slice(2)}`;
};

export const AddCardPage = memo(() => {
  useClaimLink();
  useVisualViewportPin();
  const keyboardOpen = useVirtualKeyboardOpen();
  const { theme } = useStore();
  const { session } = useSessionStore();
  const { redirectTo } = useRouteWithToken();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = (location.state as { returnTo?: string } | null)?.returnTo;
  const leaveCardForm = () => {
    if (returnTo) {
      redirectTo(returnTo, { replace: true });
      return;
    }
    navigate(-1);
  };
  const { t } = useTranslation();

  useEffect(() => {
    safeCapture(getEvents().ADD_CARD_VIEW);
  }, []);

  const allowedBrands = (session.data?.config?.cardPolicy?.allowedCardBrands ??
    DEFAULT_ALLOWED_BRANDS) as CardBrandKey[];

  const [form, setForm] = useState<CardStepFields>({
    cardNumber: "",
    expiry: "",
    cardHolderName: "",
    cvv: "",
  });
  const [errors, setErrors] = useState<CardFormErrors>({});

  const colorsConfig = theme?.whiteView;
  const { backgroundBtn, textColorBtn, bodyCopy } = colorsConfig;
  const fieldColors = {
    background: colorsConfig.background,
    title: colorsConfig.title,
    bodyCopy,
    inactiveColor: colorsConfig.subtitile,
    errorColor: colorsConfig.errorColor,
  };

  const detectedBrand = useMemo(
    () => detectCardBrand(form.cardNumber),
    [form.cardNumber]
  );
  const detectedBrandInfo =
    detectedBrand && allowedBrands.includes(detectedBrand)
      ? CARD_BRANDS[detectedBrand]
      : null;

  const brandsLabel = allowedBrands
    .map((key) => CARD_BRANDS[key]?.label)
    .filter(Boolean)
    .join(", ");

  const handleChange = (field: keyof CardStepFields, value: string) => {
    const next =
      field === "cardHolderName" ? formatCardholderNameInput(value) : value;
    setForm((prev) => ({ ...prev, [field]: next }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validate = (): CardFormErrors => {
    const errs: CardFormErrors = {};
    const cleaned = form.cardNumber.replace(/\s/g, "");
    if (!/^\d{13,19}$/.test(cleaned)) {
      errs.cardNumber = t("card.invalidNumber");
    } else {
      const brand = detectCardBrand(form.cardNumber);
      if (!brand || !allowedBrands.includes(brand)) {
        errs.cardNumber = t("card.brandNotAccepted");
      }
    }

    const [mm, yy] = form.expiry.split("/");
    if (!mm || !yy || !/^(0[1-9]|1[0-2])$/.test(mm) || !/^\d{2}$/.test(yy)) {
      errs.expiry = t("card.invalidExpiry");
    } else {
      const now = new Date();
      const currentMonth = now.getMonth() + 1;
      const currentYear = now.getFullYear() % 100;
      const expMonth = parseInt(mm, 10);
      const expYear = parseInt(yy, 10);
      if (
        expYear < currentYear ||
        (expYear === currentYear && expMonth < currentMonth)
      ) {
        errs.expiry = t("card.expiredCard");
      }
    }

    if (!form.cardHolderName.trim()) {
      errs.cardHolderName = t("card.nameRequired");
    } else if (!isValidCardholderName(form.cardHolderName)) {
      errs.cardHolderName = t("card.nameInvalid");
    }
    if (!/^\d{3,4}$/.test(form.cvv)) errs.cvv = t("card.invalidCvv");

    return errs;
  };

  const handleSubmit = (event?: React.FormEvent) => {
    event?.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    redirectTo(APP_ROUTES.ADD_CARD_ADDRESS_PAGE, { state: { card: form } });
  };

  const errorStyle: React.CSSProperties = {
    color: "#dc251c",
    fontSize: "12px",
    marginTop: "4px",
  };

  return (
    <AuraLayout colorConfig={colorsConfig}>
      <Layout.Header>
        <Navbar
          align="center"
          color={TITLE_COLOR}
          title={t("card.add")}
          onBackClick={leaveCardForm}
        />
      </Layout.Header>

      <Layout.Content className="collect">
        <ScrollFadeIndicator color={bodyCopy} />
        <form id="add-card-form" className="mock-card-form" onSubmit={handleSubmit}>
          <section className="mock-card-fields">
            <div className="mock-card-field">
              <FloatingField
                label={t("card.owner")}
                value={form.cardHolderName}
                required
                isValid={!errors.cardHolderName}
                colors={fieldColors}
              >
                <input
                  id="card-holder-name"
                  style={{ color: bodyCopy }}
                  value={form.cardHolderName}
                  onChange={(e) => handleChange("cardHolderName", e.target.value)}
                  maxLength={CARDHOLDER_NAME_INPUT_MAX_LENGTH}
                  autoComplete="cc-name"
                  autoCapitalize="characters"
                  spellCheck={false}
                  autoFocus
                />
              </FloatingField>
              {errors.cardHolderName && (
                <div style={errorStyle}>{errors.cardHolderName}</div>
              )}
            </div>

            <div className="mock-card-field">
              <FloatingField
                label={t("card.number")}
                placeholder={t("card.numberPlaceholder")}
                value={form.cardNumber}
                required
                isValid={!errors.cardNumber}
                colors={fieldColors}
                adornment={
                  detectedBrandInfo ? (
                    <img
                      className="mock-card-brand"
                      src={detectedBrandInfo.logo}
                      alt={t(detectedBrandInfo.altKey)}
                    />
                  ) : undefined
                }
              >
                <input
                  id="card-number"
                  style={{ color: bodyCopy, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", letterSpacing: "0.04em" }}
                  value={form.cardNumber}
                  onChange={(e) =>
                    handleChange("cardNumber", formatCardNumber(e.target.value))
                  }
                  inputMode="numeric"
                  maxLength={23}
                />
              </FloatingField>
              {errors.cardNumber && (
                <div style={errorStyle}>{errors.cardNumber}</div>
              )}
            </div>

            <section className="vgs-inline">
              <div className="mock-card-field" style={{ flex: 1 }}>
                <FloatingField
                  label={t("card.expiryLabel")}
                  placeholder={t("card.expiryPlaceholder")}
                  value={form.expiry}
                  required
                  isValid={!errors.expiry}
                  colors={fieldColors}
                >
                  <input
                    id="card-expiry"
                    style={{ color: bodyCopy, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", letterSpacing: "0.04em" }}
                    value={form.expiry}
                    onChange={(e) =>
                      handleChange("expiry", formatExpiry(e.target.value))
                    }
                    inputMode="numeric"
                  />
                </FloatingField>
                {errors.expiry && <div style={errorStyle}>{errors.expiry}</div>}
              </div>
              <div className="mock-card-field" style={{ flex: 1 }}>
                <FloatingField
                  label={t("card.cvvLabel")}
                  placeholder={t("card.cvvFormatPlaceholder")}
                  value={form.cvv}
                  required
                  isValid={!errors.cvv}
                  colors={fieldColors}
                >
                  <input
                    id="card-cvv"
                    style={{ color: bodyCopy, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", letterSpacing: "0.04em" }}
                    type="password"
                    value={form.cvv}
                    onChange={(e) =>
                      handleChange("cvv", e.target.value.replace(/\D/g, "").slice(0, 4))
                    }
                    inputMode="numeric"
                    autoComplete="cc-csc"
                    maxLength={4}
                  />
                </FloatingField>
                {errors.cvv && <div style={errorStyle}>{errors.cvv}</div>}
              </div>
            </section>
          </section>
        </form>
      </Layout.Content>

      <Layout.Footer>
        <section className="valid-cards">
          {!keyboardOpen && (
          <section className="valid-cards-container">
            <section className="valid-cards-logos">
              {allowedBrands.map((key) => {
                const brand = CARD_BRANDS[key];
                if (!brand) return null;
                return <img key={key} src={brand.logo} alt={t(brand.altKey)} />;
              })}
            </section>
            <BodyCopy className="footer-text">
              {t("card.acceptedBrands", { brands: brandsLabel })}
            </BodyCopy>
          </section>
          )}
          <section className="footer-section">
            <Button
              size="large"
              color={textColorBtn}
              background={backgroundBtn}
              onPointerDown={blurActiveTextInput}
              onClick={() => handleSubmit()}
            >
              {t("card.continue")}
            </Button>
          </section>
        </section>
      </Layout.Footer>
    </AuraLayout>
  );
});
